import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { adminRequest, type PaginatedResponse } from "../adminApi";
import { LoadingPanel } from "../components/LoadingPanel";
import { Pagination } from "../components/Pagination";

type LogRow = {
  id: string;
  created_at: string;
  event_type: string;
  metadata: Record<string, unknown> | null;
  user_email: string | null;
};

export function LogsPage({
  mfaToken,
  session,
  setNotice
}: {
  mfaToken: string | null;
  session: Session;
  setNotice(n: { tone: "error" | "success"; text: string } | null): void;
}) {
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [eventType, setEventType] = useState("");
  const pageSize = 50;

  async function load() {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params = new URLSearchParams({ offset: String(offset), limit: String(pageSize) });
      if (eventType) params.set("event_type", eventType);
      const data = await adminRequest<PaginatedResponse<LogRow>>(`/logs?${params}`, { mfaToken, session });
      setLogs(data.logs ?? []);
      setTotal(data.total_count);
    } catch (caught) {
      setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Could not load logs." });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [page, eventType]);

  return (
    <div className="stack">
      <section className="section">
        <div className="sectionHead">
          <div>
            <h1>Audit Logs</h1>
            <p>System-wide audit events, filterable by type.</p>
          </div>
          <button onClick={() => void load()} type="button">Refresh</button>
        </div>

        <div className="filterBar">
          <select value={eventType} onChange={(e) => { setEventType(e.target.value); setPage(1); }}>
            <option value="">All event types</option>
            <option value="run_created">run_created</option>
            <option value="run_completed">run_completed</option>
            <option value="run_cancelled">run_cancelled</option>
            <option value="vehicle_linked">vehicle_linked</option>
            <option value="vehicle_unlinked">vehicle_unlinked</option>
            <option value="key_paired">key_paired</option>
            <option value="schedule_created">schedule_created</option>
            <option value="schedule_updated">schedule_updated</option>
            <option value="invitation_sent">invitation_sent</option>
            <option value="invitation_redeemed">invitation_redeemed</option>
            <option value="widget_token_created">widget_token_created</option>
            <option value="widget_token_revoked">widget_token_revoked</option>
            <option value="user_deleted">user_deleted</option>
          </select>
        </div>

        {loading ? <LoadingPanel label="Loading logs…" /> : (
          <>
            <table className="wtable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Event</th>
                  <th>User</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="cellMono">{new Date(log.created_at).toISOString().slice(0, 19).replace("T", " ")}</td>
                    <td><span className="tag tag--event">{log.event_type}</span></td>
                    <td>{log.user_email ?? "—"}</td>
                    <td className="cellMeta">{log.metadata ? JSON.stringify(log.metadata) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination currentPage={page} pageSize={pageSize} totalCount={total} onPageChange={setPage} />
          </>
        )}
      </section>
    </div>
  );
}
